import { extendTheme, type ThemeConfig } from '@chakra-ui/react';
import styleguide from '@common/styleguide';
import { pxToRem, toPx } from '@common/util/styles';
import chroma from 'chroma-js';
import { fontFamily } from '../typography';
import Accordion from './components/accordion';
import Alert from './components/alert';
import Button from './components/button';
import Checkbox from './components/checkbox';
import Divider from './components/divider';
import FormLabel from './components/form-label';
import Input from './components/input';
import Kbd from './components/kbd';
import List from './components/list';
import Menu from './components/menu';
import Modal from './components/modal';
import Select from './components/select';
import Skeleton from './components/skeleton';
import Switch from './components/switch';
import Table from './components/table';
import Tabs from './components/tabs';
import Tooltip from './components/tooltip';

interface ThemeParams {
    [key: string]: any;

    config: ThemeConfig;
}

const baseThemeParams: ThemeParams = {
    colors: {
        // https://chakra-ui.com/docs/styled-system/theme#colors
        white: styleguide.colors.white,
        whiteAlpha: {
            50: chroma(styleguide.colors.white).alpha(0.05).hex(),
            100: chroma(styleguide.colors.white).alpha(0.1).hex(),
            200: chroma(styleguide.colors.white).alpha(0.2).hex(),
            300: chroma(styleguide.colors.white).alpha(0.3).hex(),
            400: chroma(styleguide.colors.white).alpha(0.4).hex(),
            500: chroma(styleguide.colors.white).alpha(0.5).hex(),
            600: chroma(styleguide.colors.white).alpha(0.6).hex(),
            700: chroma(styleguide.colors.white).alpha(0.7).hex(),
            800: chroma(styleguide.colors.white).alpha(0.8).hex(),
        },
        black: styleguide.colors.black,
        blackAlpha: {
            50: chroma(styleguide.colors.black).alpha(0.05).hex(),
            100: chroma(styleguide.colors.black).alpha(0.1).hex(),
            200: chroma(styleguide.colors.black).alpha(0.2).hex(),
            300: chroma(styleguide.colors.black).alpha(0.3).hex(),
            400: chroma(styleguide.colors.black).alpha(0.4).hex(),
            500: chroma(styleguide.colors.black).alpha(0.5).hex(),
            600: chroma(styleguide.colors.black).alpha(0.6).hex(),
            700: chroma(styleguide.colors.black).alpha(0.7).hex(),
            800: chroma(styleguide.colors.black).alpha(0.8).hex(),
        },
        gray: {
            50: styleguide.colors.grayLight4X,
            100: styleguide.colors.grayLight3X,
            200: styleguide.colors.grayLight2X,
            300: styleguide.colors.grayLightX,
            400: styleguide.colors.grayLight,
            500: styleguide.colors.gray,
            600: styleguide.colors.grayDark,
            700: styleguide.colors.grayDarkX,
            800: styleguide.colors.black,
        },
        blue: {
            200: styleguide.colors.blueLight2X,
            300: styleguide.colors.blueLightX,
            400: styleguide.colors.blueLight,
            500: styleguide.colors.blue,
            600: styleguide.colors.blueDark,
        },
        indigo: {
            200: styleguide.colors.indigoLight,
            500: styleguide.colors.indigo,
        },
        teal: {
            500: styleguide.colors.teal,
        },
        green: {
            100: styleguide.colors.greenLighter,
            200: styleguide.colors.greenLight,
            300: styleguide.colors.greenBright,
            500: styleguide.colors.green,
            600: styleguide.colors.greenDark,
            700: styleguide.colors.greenDarker,
            800: styleguide.colors.greenDark2X,
        },
        yellow: {
            200: styleguide.colors.yellowLight,
            500: styleguide.colors.yellow,
            600: styleguide.colors.yellowDark,
        },
        orange: {
            200: styleguide.colors.orangeLight,
            300: styleguide.colors.orangeBright,
            700: styleguide.colors.orangeDark,
        },
        red: {
            300: styleguide.colors.redLight,
            500: styleguide.colors.red,
            600: styleguide.colors.redDark,
        },
        pink: {
            500: styleguide.colors.pink,
        },
    },
    semanticTokens: {
        // https://chakra-ui.com/docs/styled-system/semantic-tokens
        colors: {
            ...styleguide.semantic,
            text: {
                default: styleguide.semantic.textDark,
                _dark: styleguide.semantic.textLight,
            },
            textMuted: {
                default: styleguide.semantic.textGrayOnLight,
                _dark: styleguide.semantic.textGrayOnDark,
            },
            textMuted2X: {
                default: styleguide.semantic.textGrayLightOnLight,
                _dark: styleguide.semantic.textGrayLightOnDark,
            },
            textGrayAlpha: chroma(styleguide.colors.gray).alpha(0.5).hex(),
        },
    },
    fonts: {
        // https://chakra-ui.com/docs/styled-system/theme#typography
        body: fontFamily.font,
        heading: fontFamily.font,
        mono: fontFamily.monoFont,
        serif: fontFamily.serifFont,
    },
    fontSizes: {
        // https://chakra-ui.com/docs/styled-system/theme#typography
        '2xs': pxToRem(toPx(styleguide.typography.fontSizes['2xs'])),
        xs: pxToRem(toPx(styleguide.typography.fontSizes.xs)),
        sm: pxToRem(toPx(styleguide.typography.fontSizes.sm)),
        md: pxToRem(toPx(styleguide.typography.fontSizes.md)),
        lg: pxToRem(toPx(styleguide.typography.fontSizes.lg)),
        lgr: pxToRem(toPx(styleguide.typography.fontSizes.lgr)),
        xl: pxToRem(toPx(styleguide.typography.fontSizes.xl)),
        '2xl': pxToRem(toPx(styleguide.typography.fontSizes['2xl'])),
        '3xl': pxToRem(toPx(styleguide.typography.fontSizes['3xl'])),
        '4xl': pxToRem(toPx(styleguide.typography.fontSizes['4xl'])),
        '5xl': pxToRem(toPx(styleguide.typography.fontSizes['5xl'])),
        '6xl': pxToRem(toPx(styleguide.typography.fontSizes['6xl'])),
        '7xl': pxToRem(toPx(styleguide.typography.fontSizes['7xl'])),
        '8xl': pxToRem(toPx(styleguide.typography.fontSizes['8xl'])),
    },
    fontWeights: {
        // https://chakra-ui.com/docs/styled-system/theme#typography
        ...styleguide.typography.fontWeights,
    },
    letterSpacings: {
        // https://chakra-ui.com/docs/styled-system/theme#typography
        ...styleguide.typography.letterSpacings,
    },
    breakpoints: {
        // https://chakra-ui.com/docs/styled-system/theme#breakpoints
    },
    // https://chakra-ui.com/docs/styled-system/theme#spacing
    space: {
        // Adds some additional mid-size spacings omitted by Chakra
        11: '2.75rem',
        13: '3.25rem',
        15: '3.75rem',
        18: '4.5rem',
        22: '5.5rem',
        26: '6.5rem',
    },
    // https://chakra-ui.com/docs/styled-system/theme#sizes
    sizes: {
        // Adds some additional mid-size sizings omitted by Chakra
        11: '2.75rem',
        13: '3.25rem',
        15: '3.75rem',
        18: '4.5rem',
        22: '5.5rem',
        26: '6.5rem',
    },
    borderRadius: {
        // https://chakra-ui.com/docs/styled-system/theme#border-radius
    },
    zIndices: {
        // https://chakra-ui.com/docs/styled-system/theme#z-index-values
    },
    shadows: {
        // https://github.com/chakra-ui/chakra-ui/blob/main/packages/components/theme/src/foundations/shadows.ts
        xs: '0 0 0 1px rgba(0, 0, 0, 0.05)',
        sm: '0 4px 6px 0 rgba(0, 0, 0, 0.05)',
        base: '0 4px 6px 0 rgba(0, 0, 0, 0.05)',
        md: '0 18px 24px 0 rgba(0, 0, 0, 0.07)',
        lg: '0 24px 32px 0 rgba(0,0,0,0.12)', // Edited based on Mark's feedback - all others from Chakra
        xl: '0 20px 25px 0 rgba(0, 0, 0, 0.12), 0 10px 10px 0 rgba(0, 0, 0, 0.04)',
        '2xl': '0 25px 50px 0 rgba(0, 0, 0, 0.25)',
        outline: '0 0 0 3px rgba(66, 153, 225, 0.6)',
        inner: 'inset 0 2px 4px 0 rgba(0,0,0,0.06)',
        none: 'none',
        'dark-lg':
            'rgba(0, 0, 0, 0.1) 0px 0px 0px 1px, rgba(0, 0, 0, 0.2) 0px 5px 10px, rgba(0, 0, 0, 0.4) 0px 15px 40px',
    },
    config: {
        initialColorMode: 'light',
        useSystemColorMode: false,
    },
    styles: {
        global: {
            html: {},
            body: {
                bg: styleguide.semantic.bgLight,
                color: styleguide.semantic.textDark,
            },
        },
    },
    components: {
        // Defaults: https://github.com/chakra-ui/chakra-ui/tree/main/packages/components/theme/src/components
        Accordion,
        Alert,
        Button,
        Checkbox,
        Divider,
        FormLabel,
        Input,
        Kbd,
        List,
        Menu,
        Modal,
        Select,
        Skeleton,
        Switch,
        Table,
        Tabs,
        Tooltip,
    },
};

const theme = extendTheme(baseThemeParams);

/**
 * Chakra comes preloaded with a bunch of colors we don't care about.
 * Until we decide to explicitly declare *every* color we want, this hack
 * removes colorschemes we *know* we don't want.
 */
const UNUSED_COLORS = [
    'whatsapp',
    'linkedin',
    'facebook',
    'messenger',
    'twitter',
    'telegram',
];
UNUSED_COLORS.forEach((c) => {
    delete theme.colors[c];
});

export default theme;
